import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Compass, ChevronLeft, ChevronRight, Grid, ArrowRight } from "lucide-react";
import LaunchesOverviewMap from "@/components/LaunchesOverviewMap";
import { useSgdChfRate, convertSgdText } from "@/lib/currency";

export const LAUNCH_PROJECTS = [
  {
    id: "emerald",
    project: "Emerald of Katong",
    shortName: "Emerald",
    district: "D15 East Coast",
    region: "East Coast",
    locationDesc: "Jalan Tembusu / Tanjong Katong",
    lat: 1.304747,
    lng: 103.89804,
    sold: "99%",
    soldNum: 99,
    units: "841 of 846 units sold in 48 hours",
    psf: "S$2,580 psf",
    gross: "S$1.5 Billion in gross sales",
    statusBadge: "Mega-Launch Record",
    color: "bg-emerald-500",
    image: `${process.env.PUBLIC_URL || ""}/images/launches/emerald-of-katong.jpg`,
    fallbackImage: "https://emerald-ofkatong.com.sg/wp-content/uploads/2024/10/Emerald-of-Katong-condo-at-jalan-tembusu-by-sim-lian-singapore-1-1024x576.jpeg",
  },
  {
    id: "jden",
    project: "J'den",
    shortName: "J'den",
    district: "D22 Jurong Lake",
    region: "West Regional Hub",
    locationDesc: "Jurong Gateway / Jurong East MRT",
    lat: 1.333355,
    lng: 103.740182,
    sold: "88%",
    soldNum: 88,
    units: "323 of 368 units sold on Day 1",
    psf: "S$2,451 psf",
    gross: "Shattered regional benchmark",
    statusBadge: "Jurong Lake District Benchmark",
    color: "bg-emerald-500",
    image: `${process.env.PUBLIC_URL || ""}/images/launches/jden.jpg`,
    fallbackImage: "https://www.wohhup.com/wp-content/uploads/2023/06/Jden-condo-tower-view.jpg",
  },
  {
    id: "norwood",
    project: "Norwood Grand",
    shortName: "Norwood",
    district: "D25 Woodlands",
    region: "North Gateway",
    locationDesc: "Champions Way / Woodlands South MRT",
    lat: 1.428537,
    lng: 103.790837,
    sold: "84%",
    soldNum: 84,
    units: "292 of 348 units sold launch weekend",
    psf: "S$2,067 psf",
    gross: "Highest sold launch in North region",
    statusBadge: "RTS Link Growth Corridor",
    color: "bg-emerald-500",
    image: `${process.env.PUBLIC_URL || ""}/images/launches/norwood-grand.webp`,
    fallbackImage: "https://norwoodgrand-condo.sg/wp-content/uploads/2024/10/Norwood-Grand-Condo-Facade.jpg",
  },
  {
    id: "piccadilly",
    project: "Piccadilly Grand",
    shortName: "Piccadilly",
    district: "D08 Farrer Park",
    region: "City Fringe",
    locationDesc: "Northumberland Rd / Farrer Park MRT",
    lat: 1.31125,
    lng: 103.852455,
    sold: "77%",
    soldNum: 77,
    units: "315 of 407 units sold launch weekend",
    psf: "S$2,150 psf",
    gross: "CDL & MCL Land joint venture",
    statusBadge: "Direct MRT Link",
    color: "bg-emerald-500",
    image: `${process.env.PUBLIC_URL || ""}/images/launches/piccadilly-grand.jpg`,
    fallbackImage: "https://piccadilly-grand-condo.com.sg/wp-content/uploads/2022/04/Piccadilly-Grand-Exterior-Night.jpg",
  },
  {
    id: "chuan",
    project: "Chuan Park",
    shortName: "Chuan Park",
    district: "D19 Serangoon",
    region: "Central-East",
    locationDesc: "Lorong Chuan MRT / Serangoon",
    lat: 1.352513,
    lng: 103.864455,
    sold: "76%",
    soldNum: 76,
    units: "696 of 916 units sold on opening day",
    psf: "S$2,579 psf",
    gross: "S$1.16 Billion in 24 hours",
    statusBadge: "Massive Upgrader Velocity",
    color: "bg-emerald-500",
    image: `${process.env.PUBLIC_URL || ""}/images/launches/chuan-park.jpg`,
    fallbackImage: "https://www.thechuan-park.com.sg/wp-content/uploads/2024/09/chuan-park-condo-former-chuan-park-en-bloc-by-kingsford-arrival-courtyard-singapore-scaled.jpg",
  },
  {
    id: "lentor",
    project: "Lentor Mansion",
    shortName: "Lentor",
    district: "D26 Lentor Hills",
    region: "North-Central",
    locationDesc: "Lentor Gardens / Lentor MRT",
    lat: 1.383275,
    lng: 103.833148,
    sold: "75%",
    soldNum: 75,
    units: "400 of 533 units sold launch weekend",
    psf: "S$2,283 psf",
    gross: "100% of 2-bed units gone in hours",
    statusBadge: "Lentor Hills Pre-Sales Peak",
    color: "bg-emerald-500",
    image: `${process.env.PUBLIC_URL || ""}/images/launches/lentor-mansion.jpg`,
    fallbackImage: "https://s3.ap-southeast-1.amazonaws.com/propnex-xserver-img/pnimgs/fileservice/origin-file/1013566/admin-property/2509/1013566-admin-property-1758099953-Exterior4.jpg",
  },
  {
    id: "watten",
    project: "Watten House",
    shortName: "Watten",
    district: "D11 Bukit Timah",
    region: "Core Central Region",
    locationDesc: "Shelford Road / Tan Kah Kee MRT",
    lat: 1.329245,
    lng: 103.810942,
    sold: "74%",
    soldNum: 74,
    units: "133 of 180 units sold launch period",
    psf: "S$3,250 psf",
    gross: "102 units sold on preview day alone",
    statusBadge: "Prime CCR Freehold Trophy",
    color: "bg-emerald-500",
    image: `${process.env.PUBLIC_URL || ""}/images/launches/watten-house.webp`,
    fallbackImage: "https://watten-house.com.sg/wp-content/uploads/2023/10/Watten-House-Perspective-1.jpg",
  },
  {
    id: "blossoms",
    project: "Blossoms by the Park",
    shortName: "Blossoms",
    district: "D05 One-North",
    region: "West Innovation Hub",
    locationDesc: "Slim Barracks Rise / Buona Vista MRT",
    lat: 1.304394,
    lng: 103.789298,
    sold: "73%",
    soldNum: 73,
    units: "200 of 275 units sold launch weekend",
    psf: "S$2,423 psf",
    gross: "Strong demand from One-North biomedical hub",
    statusBadge: "One-North Knowledge District",
    color: "bg-emerald-500",
    image: `${process.env.PUBLIC_URL || ""}/images/launches/blossoms-by-the-park.jpg`,
    fallbackImage: "https://stacked-editorial.sgp1.digitaloceanspaces.com/editorial/wp-content/uploads/2023/04/28142122/Blossoms-by-the-Park-9-model-16x9-1.jpg",
  },
  {
    id: "reserve",
    project: "The Reserve Residences",
    shortName: "Reserve",
    district: "D21 Beauty World",
    region: "Central-West Hub",
    locationDesc: "Jalan Anak Bukit / Beauty World MRT",
    lat: 1.340842,
    lng: 103.777683,
    sold: "71%",
    soldNum: 71,
    units: "520 of 732 units sold launch weekend",
    psf: "S$2,460 psf",
    gross: "S$1 Billion sold on opening weekend",
    statusBadge: "Integrated Transport Hub",
    color: "bg-emerald-500",
    image: `${process.env.PUBLIC_URL || ""}/images/launches/the-reserve-residences.jpg`,
    fallbackImage: "https://www.thereserveresidences-sg.com/wp-content/uploads/2023/05/The-Reserve-Residences-Facade.jpg",
  },
  {
    id: "sceneca",
    project: "Sceneca Residence",
    shortName: "Sceneca",
    district: "D16 Tanah Merah",
    region: "East Corridor",
    locationDesc: "Tanah Merah Kechil Link / MRT",
    lat: 1.327884,
    lng: 103.945379,
    sold: "60%",
    soldNum: 60,
    units: "160 of 268 units sold on Day 1",
    psf: "S$2,072 psf",
    gross: "Direct sheltered link to Tanah Merah MRT",
    statusBadge: "Direct MRT Retail Hub",
    color: "bg-[#C5A880]",
    image: `${process.env.PUBLIC_URL || ""}/images/launches/sceneca-residence.jpg`,
    fallbackImage: "https://thescenecaresidences.com.sg/wp-content/uploads/2022/11/Sceneca-Residence-2.jpg",
  },
  {
    id: "dunman",
    project: "Grand Dunman",
    shortName: "Dunman",
    district: "D15 Dunman",
    region: "City Fringe",
    locationDesc: "Dunman Road / Dakota MRT",
    lat: 1.308302,
    lng: 103.891698,
    sold: "55%",
    soldNum: 55,
    units: "550 units sold on Day 1",
    psf: "S$2,500 psf",
    gross: "Biggest mega-launch weekend of 2023",
    statusBadge: "Prime City-Fringe Scale",
    color: "bg-[#C5A880]",
    image: `${process.env.PUBLIC_URL || ""}/images/launches/grand-dunman.jpg`,
    fallbackImage: "https://the-granddunman.com.sg/wp-content/uploads/2023/06/GrandDunman-Perspective-1-1024x556.jpg",
  },
  {
    id: "tembusu",
    project: "Tembusu Grand",
    shortName: "Tembusu",
    district: "D15 Tanjong Katong",
    region: "East Coast Heritage",
    locationDesc: "Jalan Tembusu / Tanjong Katong MRT",
    lat: 1.305281,
    lng: 103.896716,
    sold: "53%",
    soldNum: 53,
    units: "340 of 638 units sold launch weekend",
    psf: "S$2,465 psf",
    gross: "CDL & MCL Land East Coast flagship",
    statusBadge: "Katong Enclave Benchmark",
    color: "bg-[#C5A880]",
    image: `${process.env.PUBLIC_URL || ""}/images/launches/tembusu-grand.webp`,
    fallbackImage: "https://tembusugrand-condo.sg/wp-content/uploads/2023/07/Tembusu-Grand-Facade-Aerial-View.jpg",
  },
  {
    id: "kassia",
    project: "Kassia",
    shortName: "Kassia",
    district: "D17 Flora Drive",
    region: "Far East / Changi",
    locationDesc: "Flora Drive / Pasir Ris",
    lat: 1.357096,
    lng: 103.965502,
    sold: "52%",
    soldNum: 52,
    units: "144 of 276 units sold launch weekend",
    psf: "S$2,024 psf",
    gross: "Final parcel in Flora Drive resort cluster",
    statusBadge: "Freehold Resort Living",
    color: "bg-[#C5A880]",
    image: `${process.env.PUBLIC_URL || ""}/images/launches/kassia.webp`,
    fallbackImage: "https://kassiacondo.com.sg/wp-content/uploads/2024/06/Kassia-Condo-Facade-Aerial-View.jpg",
  },
  {
    id: "meyer",
    project: "Meyer Blue",
    shortName: "Meyer Blue",
    district: "D15 Meyer Road",
    region: "Prime East Coast Seafront",
    locationDesc: "81 Meyer Road / Katong Park MRT",
    lat: 1.296583,
    lng: 103.891683,
    sold: "50%",
    soldNum: 50,
    units: "114 of 226 units sold on Day 1",
    psf: "S$3,260 psf",
    gross: "Over 50% sold in prestigious freehold seafront belt",
    statusBadge: "Prime Freehold Waterfront",
    color: "bg-[#C5A880]",
    image: `${process.env.PUBLIC_URL || ""}/images/launches/meyer-blue.jpeg`,
    fallbackImage: "https://www.meyersblue.com.sg/wp-content/uploads/2024/09/meyer-blue-facade-singapore.jpeg",
  },
  {
    id: "pinetree",
    project: "Pinetree Hill",
    shortName: "Pinetree",
    district: "D21 Mount Sinai",
    region: "Bukit Timah Fringe",
    locationDesc: "Pine Grove / Ulu Pandan",
    lat: 1.319872,
    lng: 103.776658,
    sold: "38%",
    soldNum: 38,
    units: "150 of 400 released units on launch weekend",
    psf: "S$2,460 psf",
    gross: "UOL & SingLand lush elevated sanctuary",
    statusBadge: "Mount Sinai Green Sanctuary",
    color: "bg-amber-500",
    image: `${process.env.PUBLIC_URL || ""}/images/launches/pinetree-hill.jpeg`,
    fallbackImage: "https://pinetree-hill-sg.com/wp-content/uploads/2023/06/Pinetree-Hill-Facade.jpeg",
  },
];

export default function SingaporeLaunchMap() {
  const chfRate = useSgdChfRate();
  const [activeId, setActiveId] = useState("emerald");
  const [viewMode, setViewMode] = useState("map"); // "map" | "grid"

  const activeProject = LAUNCH_PROJECTS.find((p) => p.id === activeId) || LAUNCH_PROJECTS[0];
  const activeIndex = LAUNCH_PROJECTS.findIndex((p) => p.id === activeId);

  const handleNext = () => {
    const nextIdx = (activeIndex + 1) % LAUNCH_PROJECTS.length;
    setActiveId(LAUNCH_PROJECTS[nextIdx].id);
  };

  const handlePrev = () => {
    const prevIdx = (activeIndex - 1 + LAUNCH_PROJECTS.length) % LAUNCH_PROJECTS.length;
    setActiveId(LAUNCH_PROJECTS[prevIdx].id);
  };

  return (
    <div className="bg-[#FAF8F5] border border-[#E7E2D6] rounded-3xl p-6 sm:p-8 lg:p-10 mb-16 shadow-xs">
      {/* Header with View Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <span className="text-xs font-mono uppercase text-[#C5A880] font-bold tracking-wider flex items-center gap-1.5">
            <Compass className="w-3.5 h-3.5" />
            Geographic Absorption &amp; Market Footprint
          </span>
          <h3 className="font-serif-display text-2xl sm:text-3xl text-[#0A1128] font-medium mt-1">
            Launch Weekend Sell-Out Rates
          </h3>
        </div>

        <div className="flex items-center gap-3">
          <span className="hidden md:inline-flex bg-emerald-100 text-emerald-800 text-xs font-semibold px-3 py-1 rounded-full w-fit">
            High Liquidity &amp; Instant Absorption
          </span>

          {/* View Toggle */}
          <div className="inline-flex items-center p-1 bg-white border border-[#E7E2D6] rounded-xl text-xs font-mono">
            <button
              onClick={() => setViewMode("map")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all ${
                viewMode === "map"
                  ? "bg-[#0A1128] text-[#C5A880] font-semibold shadow-xs"
                  : "text-slate-600 hover:text-[#0A1128]"
              }`}
            >
              <MapPin className="w-3.5 h-3.5" />
              Singapore Map
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all ${
                viewMode === "grid"
                  ? "bg-[#0A1128] text-[#C5A880] font-semibold shadow-xs"
                  : "text-slate-600 hover:text-[#0A1128]"
              }`}
            >
              <Grid className="w-3.5 h-3.5" />
              Card Grid
            </button>
          </div>
        </div>
      </div>

      {viewMode === "map" ? (
        <div>
          {/* Project Selector Pills */}
          <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-6 scrollbar-thin">
            {LAUNCH_PROJECTS.map((p) => {
              const isSelected = p.id === activeId;
              return (
                <button
                  key={p.id}
                  onClick={() => setActiveId(p.id)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-mono shrink-0 transition-all flex items-center gap-2 border ${
                    isSelected
                      ? "bg-[#0A1128] text-white border-[#0A1128] shadow-sm font-semibold scale-102"
                      : "bg-white text-slate-600 border-[#E7E2D6] hover:border-[#C5A880] hover:text-[#0A1128]"
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${
                      isSelected
                        ? "bg-[#C5A880] animate-pulse"
                        : p.soldNum >= 70
                        ? "bg-emerald-500"
                        : p.soldNum >= 50
                        ? "bg-[#C5A880]"
                        : "bg-amber-500"
                    }`}
                  />
                  <span>{p.shortName || p.project.split(" ")[0]}</span>
                  <span
                    className={`text-[10px] px-1.5 py-0.2 rounded font-bold ${
                      isSelected
                        ? "bg-[#C5A880] text-[#0A1128]"
                        : p.soldNum >= 70
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {p.sold}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Interactive Map Canvas Stage */}
          <div className="relative w-full rounded-2xl overflow-hidden border border-[#E7E2D6] bg-[#F4EFE6] shadow-inner">
            {/* Background SVG Vector Map of Singapore */}
            <div className="relative w-full aspect-[1.85/1] min-h-[380px] sm:min-h-[440px] lg:min-h-[500px]">
              {/* Real-geography map: SLA OneMap tiles with verified project coordinates */}
              <LaunchesOverviewMap
                projects={LAUNCH_PROJECTS.map((p) => ({
                  id: p.id,
                  lat: p.lat,
                  lng: p.lng,
                  label: p.shortName || p.project.split(" ")[0],
                  badge: p.sold,
                  tone: p.soldNum >= 70 ? "high" : "mid",
                }))}
                activeId={activeId}
                onSelect={setActiveId}
              />

              {/* Desktop Floating Project Callout Card Anchored on Map */}
              <div className="hidden lg:block absolute bottom-5 left-5 z-[500] max-w-sm w-full bg-white/95 backdrop-blur-md border border-[#E7E2D6] rounded-2xl p-4 shadow-xl">
                <div className="flex items-center justify-between text-[11px] font-mono text-slate-500 pb-2 border-b border-[#E7E2D6]/80">
                  <span className="text-[#C5A880] font-bold uppercase tracking-wider">
                    Pinned Callout · {activeProject.region}
                  </span>
                  <span>{activeProject.district}</span>
                </div>

                <div className="mt-3 flex gap-3.5 items-center">
                  <div className="w-24 h-24 rounded-xl overflow-hidden shrink-0 border border-[#E7E2D6] bg-slate-100 relative">
                    <img
                      src={activeProject.image}
                      alt={activeProject.project}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        if (activeProject.fallbackImage && e.target.src !== activeProject.fallbackImage) {
                          e.target.src = activeProject.fallbackImage;
                        }
                      }}
                    />
                    <div className="absolute top-1 right-1 bg-black/70 text-white text-[9px] font-mono px-1 rounded">
                      {convertSgdText(activeProject.psf, chfRate)}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-mono text-slate-500 truncate">
                      {activeProject.locationDesc}
                    </div>
                    <h4 className="font-serif-display text-base font-bold text-[#0A1128] truncate mt-0.5">
                      {activeProject.project}
                    </h4>

                    <div className="flex items-baseline gap-2 mt-1.5">
                      <span className="font-serif-display text-2xl font-bold text-[#0A1128]">
                        {activeProject.sold}
                      </span>
                      <span className="text-[10px] font-mono text-slate-500 uppercase">
                        Sell-Out Rate
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden mt-1">
                      <div
                        className={`${activeProject.color} h-full rounded-full transition-all duration-500`}
                        style={{ width: activeProject.sold }}
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between text-[11px] font-mono">
                  <span className="text-slate-600 truncate">{activeProject.units}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={handlePrev}
                      className="p-1 rounded hover:bg-slate-100 text-slate-600 transition-colors"
                      title="Previous Project"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    <span className="text-[10px] text-slate-400">
                      {activeIndex + 1}/{LAUNCH_PROJECTS.length}
                    </span>
                    <button
                      onClick={handleNext}
                      className="p-1 rounded hover:bg-slate-100 text-slate-600 transition-colors"
                      title="Next Project"
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Map Footer Bar */}
            <div className="px-5 py-3 bg-white border-t border-[#E7E2D6] flex flex-wrap items-center justify-between gap-3 text-xs font-mono text-slate-600">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0" />
                  <span>&gt;70% Sold (High Liquidity)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#C5A880] shrink-0" />
                  <span>50–69% Sold (Healthy Demand)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500 shrink-0" />
                  <span>&lt;50% Sold (Selective Absorption)</span>
                </div>
              </div>
              <span className="text-slate-400 hidden lg:inline">
                Click any pin or card to inspect absorption metrics
              </span>
            </div>
          </div>

          {/* Synchronized Project Callout Cards Grid for All Devices */}
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {LAUNCH_PROJECTS.map((item) => {
              const isSelected = item.id === activeId;
              return (
                <div
                  key={item.id}
                  onClick={() => setActiveId(item.id)}
                  className={`bg-white border rounded-2xl overflow-hidden shadow-xs transition-all flex flex-col group cursor-pointer ${
                    isSelected
                      ? "border-[#0A1128] ring-2 ring-[#C5A880] shadow-lg -translate-y-1"
                      : "border-[#E7E2D6] hover:border-[#C5A880] hover:shadow-md"
                  }`}
                >
                  {/* Hero Image */}
                  <div className="h-40 overflow-hidden relative bg-slate-100">
                    <img
                      src={item.image}
                      alt={item.project}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        if (item.fallbackImage && e.target.src !== item.fallbackImage) {
                          e.target.src = item.fallbackImage;
                        }
                      }}
                    />
                    <div className="absolute top-3 left-3 bg-[#0A1128]/85 backdrop-blur px-2.5 py-1 rounded text-[10px] font-mono font-semibold text-[#C5A880]">
                      {item.district}
                    </div>
                    <div className="absolute top-3 right-3 bg-white/95 backdrop-blur px-2 py-0.5 rounded text-[10px] font-mono font-bold text-[#0A1128] shadow-xs">
                      {convertSgdText(item.psf, chfRate)}
                    </div>
                    {isSelected && (
                      <div className="absolute bottom-2 left-3 bg-[#C5A880] text-[#0A1128] text-[9px] font-mono font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm">
                        <MapPin className="w-2.5 h-2.5" /> Pinned on Map
                      </div>
                    )}
                  </div>

                  <div className="p-5 flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between text-xs font-mono text-slate-500">
                        <span className="font-bold text-[#0A1128] text-base font-serif-display group-hover:text-[#C5A880] transition-colors truncate">
                          {item.project}
                        </span>
                      </div>

                      <div className="mt-3 flex items-baseline justify-between">
                        <span className="font-serif-display text-4xl font-bold text-[#0A1128]">
                          {item.sold}
                        </span>
                        <span className="text-xs font-mono text-slate-500 font-medium">
                          Sell-Out Rate
                        </span>
                      </div>

                      {/* Progress Bar */}
                      <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mt-2">
                        <div
                          className={`${item.color} h-full rounded-full transition-all duration-700`}
                          style={{ width: item.sold }}
                        />
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-slate-100">
                      <div className="text-xs text-slate-700 font-semibold">{item.units}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">{convertSgdText(item.gross, chfRate)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* Standalone Grid View */
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {LAUNCH_PROJECTS.map((item, idx) => (
            <div
              key={idx}
              className="bg-white border border-[#E7E2D6] rounded-2xl overflow-hidden shadow-xs hover:border-[#C5A880] hover:shadow-md transition-all flex flex-col group"
            >
              {/* Hero Image */}
              <div className="h-44 overflow-hidden relative bg-slate-100">
                <img
                  src={item.image}
                  alt={item.project}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    if (item.fallbackImage && e.target.src !== item.fallbackImage) {
                      e.target.src = item.fallbackImage;
                    }
                  }}
                />
                <div className="absolute top-3 left-3 bg-[#0A1128]/85 backdrop-blur px-2.5 py-1 rounded text-[10px] font-mono font-semibold text-[#C5A880]">
                  {item.district}
                </div>
                <div className="absolute top-3 right-3 bg-white/95 backdrop-blur px-2 py-0.5 rounded text-[10px] font-mono font-bold text-[#0A1128] shadow-xs">
                  {convertSgdText(item.psf, chfRate)}
                </div>
              </div>

              <div className="p-5 flex-1 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between text-xs font-mono text-slate-500">
                    <span className="font-bold text-[#0A1128] text-base font-serif-display group-hover:text-[#C5A880] transition-colors">
                      {item.project}
                    </span>
                  </div>

                  <div className="mt-3 flex items-baseline justify-between">
                    <span className="font-serif-display text-4xl font-bold text-[#0A1128]">
                      {item.sold}
                    </span>
                    <span className="text-xs font-mono text-slate-500 font-medium">Sell-Out Rate</span>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden mt-2.5">
                    <div
                      className={`${item.color} h-full rounded-full transition-all duration-700`}
                      style={{ width: item.sold }}
                    />
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100">
                  <div className="text-xs text-slate-700 font-semibold">{item.units}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{convertSgdText(item.gross, chfRate)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Analytical Callout */}
      <div className="mt-8 flex flex-wrap items-center justify-between text-xs text-slate-600 bg-white p-4 rounded-xl border border-[#E7E2D6] gap-3">
        <span className="font-medium text-[#0A1128]">
          ★ Why do new launches sell out across all regions? Progressive payment staging over 3–4 years, backed by Singaporeans recycling HDB equity.
        </span>
        <Link to="/launches" className="text-[#C5A880] hover:text-[#0A1128] font-bold inline-flex items-center gap-1">
          Explore Full Launches Directory <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
